import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { CustomAlert, CustomButton, Text } from '../../../shared/uiComponents';
import { errorNormalizer } from '../../../shared/Helpers/functions';
import { RED } from '../../../shared/Helpers/colors';
import { AdminTypes } from '../../../components/Menu';
import AssignInfo from './assignInfo';
import DefaultBCBAConfirm from './defaultBCBAConfirmContent';
import RemoveTherapistsContent from './removeTherapistContent';

import { DispatchProperties, useSelector } from '../../../redux/store';
import {
	assignUsersToClient,
	getDefaultBCBA,
	getDefaultBCBAOptions,
	getTherapists,
	saveMeetAndGreetAuthorization,
} from '../../../redux/State/clientSlice/userClientSlice';
import { clearTherapists } from '../../../redux/State/clientSlice/clientSlice';
import { getUsersAllocationData } from '../../../redux/State/clientSlice/insuranceAuthorizationSlice';

const SaveChanges = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { clientId } = useParams();
	const [params, setParams] = useSearchParams();

	const selectedUsers = useSelector((state) => state.client.therapistsListForAssign);
	const billingUserId = useSelector((state) => state.userClient.billableUserId);
	const therapists = useSelector((state) => state.userClient.therapists);
	const [loading, setLoading] = useState<boolean>(false);

	const assignedUsers = selectedUsers.filter((x) => x.isAssigned);
	const unAssignedUsers = selectedUsers.filter((x) => !x.isAssigned && therapists?.find((y) => y.id === x.id));

	const userIds = [...assignedUsers, ...unAssignedUsers];

	const [removalConfirm, setRemovalConfirm] = useState<boolean>(false);
	const [assingConfirm, setAssignConfirm] = useState<boolean>(false);
	const [meetAndGreetProperties, setMeetAndGreetProperties] = useState<{
		isOpen: boolean;
		userIds: string[];
	}>({
		userIds: [],
		isOpen: false,
	});

	const closeAssignHandler = () => {
		params.set('page', '1');
		setParams(params);
		setAssignConfirm(false);
	};
	const closeRemovalHandler = () => setRemovalConfirm(false);

	const navigateToAllocateHandler = () => {
		if (!clientId) return;
		dispatch(clearTherapists());
		const hasAssignedBCBA = selectedUsers.some((x) => x.isAssigned && x.role === AdminTypes.bcba);

		if (!hasAssignedBCBA) {
			navigate(`/clients/client/${clientId}`);

			return;
		}
		navigate(`/clients/client/allocate-authorization/${clientId}`);
	};

	const closeMeetAndGreetHandler = () => {
		setMeetAndGreetProperties({ userIds: [], isOpen: false });
		navigateToAllocateHandler();
	};

	const meetAndGreetHandler = () => {
		if (!clientId) return;

		// If no users to process, just navigate
		if (!meetAndGreetProperties.userIds.length) {
			closeMeetAndGreetHandler();

			return;
		}

		setLoading(true);
		dispatch(
			saveMeetAndGreetAuthorization({
				clientId,
				rbtUserIds: meetAndGreetProperties.userIds,
			}),
		)
			.then(unwrapResult)
			.then(() => {
				toast('Meet and greet authorization saved');
				closeMeetAndGreetHandler();
			})
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	};

	const changeTherapistsHandler = () => {
		if (!clientId) return;

		const startDate = params.get('startDate');
		const endDate = params.get('endDate');

		dispatch(
			assignUsersToClient({
				userIds,
				clientId,
				billingUserId,
				endDate,
				startDate,
			}),
		)
			.then(unwrapResult)
			.then((response) => {
				if (!response.isSuccessful) {
					toast('Something went wrong');

					return;
				}

				toast('Success');
				dispatch(getTherapists({ clientId }));

				// Check if all users are unassigned
				if (selectedUsers.every((x) => !x.isAssigned)) {
					navigateToAllocateHandler();

					return;
				}

				// Identify therapist types
				const hasBTTherapists = selectedUsers.some((x) => x.role === AdminTypes.bt && x.isAssigned);
				const hasNonBTTherapists = selectedUsers.some((x) => x.role !== AdminTypes.bt && x.isAssigned);

				// Prepare BT user IDs for meet & greet (may be used after BCBA modal)
				const btUserIds = hasBTTherapists
					? selectedUsers.filter((x) => x.role === AdminTypes.bt && x.isAssigned).map((x) => x.id)
					: [];

				if (hasNonBTTherapists) {
					// Has BCBA/BCaBA therapists: Show BCBA modal first
					// Store BT users for potential meet & greet after BCBA modal closes
					setMeetAndGreetProperties({
						userIds: btUserIds,
						isOpen: false,
					});
					dispatch(getDefaultBCBA(clientId));
					dispatch(getDefaultBCBAOptions(clientId));
					setAssignConfirm(true);
				} else if (hasBTTherapists) {
					// Only BT therapists: Show meet & greet modal directly
					setMeetAndGreetProperties({
						userIds: btUserIds,
						isOpen: true,
					});
				} else {
					// No assigned therapists (edge case)
					navigateToAllocateHandler();
				}
			})
			.catch(errorNormalizer)
			.finally(() => {
				closeRemovalHandler();
				setLoading(false);
			});
	};

	const assginTherapistsHandler = () => {
		if (!clientId) return;
		setLoading(true);
		// eslint-disable-next-line consistent-return
		if (!unAssignedUsers.length) return changeTherapistsHandler();
		dispatch(
			getUsersAllocationData({
				clientId,
				userIds: unAssignedUsers.map((x) => x.id),
			}),
		)
			.then(unwrapResult)
			.then((response) => {
				if (response?.length) {
					setRemovalConfirm(true);

					return;
				}
				// toast("No allocated time found for provided users");
				changeTherapistsHandler();
			})
			.catch(errorNormalizer);
	};

	return (
		<div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
			<AssignInfo />
			<div style={{ width: '350px', textAlign: 'center' }}>
				<Text
					title="Changes from all tabs will be saved. Adjust provider selections as needed before saving."
					color={RED.LIGHT[16]}
					type="psm"
					fontWeight="semiBold"
					className="padding8"
				/>
				<CustomButton title="Save" onClick={assginTherapistsHandler} loading={loading} />
				<CustomAlert
					open={assingConfirm}
					onClose={closeAssignHandler}
					title="Set Default BCBA"
					Content={({ onClose }) => (
						<DefaultBCBAConfirm
							onClose={onClose}
							openMeetAndGreetAlert={() => setMeetAndGreetProperties((prev) => ({ ...prev, isOpen: true }))}
							needsToOpenMeetAndGreetAlert={meetAndGreetProperties.userIds.length > 0}
						/>
					)}
				/>
				<CustomAlert
					title="Require Meet and Greet?"
					onClose={closeMeetAndGreetHandler}
					open={Boolean(meetAndGreetProperties.isOpen)}
					Content={() => (
						<>
							<CustomButton
								title="Yes"
								onClick={meetAndGreetHandler}
								className="marginBottom16"
								disabled={loading}
								loading={loading}
							/>
							<CustomButton
								title="No"
								buttonType="secondaryGray"
								onClick={closeMeetAndGreetHandler}
								disabled={loading}
								loading={loading}
							/>
						</>
					)}
				/>
				<CustomAlert
					open={removalConfirm}
					onClose={closeRemovalHandler}
					title="Confirm Provider Removal"
					Content={({ onClose }) => (
						<RemoveTherapistsContent onClose={onClose} confirmHandler={changeTherapistsHandler} />
					)}
				/>
			</div>
		</div>
	);
};

export default SaveChanges;
